import { Link } from "react-router-dom";
import { ShieldCheck, Database, Target, Lock, UserCheck, Handshake, Mail, Trash2 } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

const CONTACT_EMAIL = "functional.lab25@gmail.com";
const LAST_UPDATED = "21 July 2026";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="fl-card p-6 mb-4" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center"
             style={{ background: "color-mix(in oklab, var(--fl-orange) 18%, transparent)", color: "var(--fl-orange)" }}>
          <Icon size={18} />
        </div>
        <h2 className="fl-heading text-lg">{title}</h2>
      </div>
      <div className="text-sm text-zinc-300 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen" style={{ background: "var(--fl-bg)" }}>
      <header className="border-b border-white/5 sticky top-0 z-30 backdrop-blur"
              style={{ background: "color-mix(in oklab, var(--fl-bg) 82%, transparent)" }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex" data-testid="privacy-brand"><Logo /></Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <Link to="/login" className="fl-btn-ghost text-xs" data-testid="privacy-signin">Sign in</Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-widest text-[#f47a20] font-bold mb-2">Privacy &amp; Data Safety</div>
          <h1 className="fl-heading text-4xl sm:text-5xl mb-3">Privacy Policy</h1>
          <p className="text-sm text-zinc-500">Last updated: {LAST_UPDATED}</p>
          <p className="text-base text-zinc-300 mt-4">
            Functional LAB is a hybrid rehabilitation clinic. This page explains what personal
            data we collect through the patient and clinician workspaces, why we collect it, and
            what you can do about it. We do not sell your data. Ever.
          </p>
        </div>

        <Section icon={Database} title="1. What data we collect" testid="privacy-collection">
          <p>To deliver rehabilitation and coaching we collect:</p>
          <ul className="list-disc list-inside space-y-1 marker:text-[#f47a20]">
            <li><strong>Account</strong> — name, email, role, authentication metadata (JWT session, hashed password or Google identity).</li>
            <li><strong>Health metrics</strong> — pain scores, fatigue, mood, sleep, range-of-motion tests, force-plate readings, wearable snapshots you connect (Apple Health / WHOOP / Garmin) and consent to share.</li>
            <li><strong>Exercise + session logs</strong> — the programme you were prescribed, sets/reps completed, adherence timestamps, and any videos you record of yourself during a session.</li>
            <li><strong>Communications</strong> — direct messages between you and your clinician, team-channel chat (clinicians only), and inquiries submitted via the public contact form.</li>
            <li><strong>Optional profile fields</strong> — sport, main goal, GP details, NHS number if you volunteer them.</li>
          </ul>
        </Section>

        <Section icon={Target} title="2. Purpose — why we use it" testid="privacy-purpose">
          <p>
            We use your data <strong>solely to adjust your rehabilitation plan and improve clinical outcomes</strong>.
            Concretely that means:
          </p>
          <ul className="list-disc list-inside space-y-1 marker:text-[#f47a20]">
            <li>Personalising the exercise programme your clinician approves for you.</li>
            <li>Flagging safety risks (e.g. pain spikes, poor-sleep streaks) so a human clinician can review.</li>
            <li>Powering the AI Recovery Coach — every AI suggestion is <strong>reviewed by a real clinician before being pushed to your plan</strong>.</li>
            <li>Sending appointment reminders and clinical follow-ups.</li>
            <li>Internal analytics on aggregated, non-identifiable outcomes.</li>
          </ul>
        </Section>

        <Section icon={Lock} title="3. Data security" testid="privacy-security">
          <p>
            Your data is <strong>encrypted in transit with TLS 1.2+</strong> and <strong>encrypted at rest</strong> on
            managed cloud infrastructure. Passwords are hashed with bcrypt (never stored in plaintext).
          </p>
          <p>
            Access to your clinical record is scoped by role — only clinicians assigned to your care can
            see your full profile, and every access is written to an append-only audit log. Admins can
            view billing + rota data but not raw clinical records unless they hold the
            <code className="mx-1 px-1 py-0.5 rounded bg-black/40 text-[11px]">clinical_admin</code>
            flag, which is currently held by a single named account.
          </p>
        </Section>

        <Section icon={UserCheck} title="4. Your rights (UK GDPR)" testid="privacy-rights">
          <p>Under UK data protection law you have the right to:</p>
          <ul className="list-disc list-inside space-y-1 marker:text-[#f47a20]">
            <li><strong>Access</strong> the personal data we hold about you — request a copy via your account settings or email {CONTACT_EMAIL}.</li>
            <li><strong>Rectify</strong> inaccurate data — edit your profile in-app or ask a clinician to correct clinical notes.</li>
            <li><strong>Be forgotten</strong> — you can permanently delete your account and all associated data using the
              <span className="inline-flex items-center gap-1 mx-1"><Trash2 size={12}/>Delete my account</span>
              button in Security Settings. This action is irreversible and completes within 30 days.</li>
            <li><strong>Object</strong> to specific processing (e.g. the AI Coach) — toggle that feature off in your consent preferences without losing access to the rest of the app.</li>
            <li><strong>Withdraw consent</strong> for optional data (wearables, video recordings, marketing) at any time.</li>
            <li><strong>Portability</strong> — export your exercise logs and messages as JSON.</li>
          </ul>
        </Section>

        <Section icon={Handshake} title="5. Third parties" testid="privacy-thirdparties">
          <p>
            We route strictly-scoped subsets of your data through the following processors, each covered
            by a data processing agreement:
          </p>
          <ul className="list-disc list-inside space-y-1 marker:text-[#f47a20]">
            <li><strong>AI models</strong> (OpenAI, Anthropic, Google) — only the message text and structured metrics
              needed for a coaching turn. We never send your name, email, NHS number, address, or GP details to any AI provider.</li>
            <li><strong>Stripe</strong> — payment card processing; we never see your full card number.</li>
            <li><strong>Resend</strong> — transactional email (password resets, appointment reminders).</li>
            <li><strong>Google Calendar / Outlook (optional)</strong> — appointment sync you explicitly connect.</li>
            <li><strong>Emergent</strong> — the platform hosting our infrastructure and Google Sign-In broker.</li>
          </ul>
          <p className="text-red-300 font-semibold">We do not sell personal data. Ever.</p>
        </Section>

        <Section icon={Mail} title="6. Contact + supervisory authority" testid="privacy-contact">
          <p>
            Data controller: <strong>Functional LAB Ltd.</strong> · Privacy contact:
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#f47a20] hover:underline mx-1">{CONTACT_EMAIL}</a>
          </p>
          <p>
            If you're unhappy with how we've handled your data you can complain to the UK
            Information Commissioner's Office at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer"
            className="text-[#f47a20] hover:underline">ico.org.uk</a>.
          </p>
        </Section>

        <div className="text-xs text-zinc-500 mt-8 flex items-center gap-2">
          <ShieldCheck size={12} className="text-[#f47a20]" />
          This policy will evolve as the product does — material changes are announced in-app 14 days before they take effect.
        </div>
      </main>
    </div>
  );
}
